import { GridArea, GridDimension } from "client/Api/Workspace/common";
import { ArgumentNotSuppliedError } from "client_legacy/ndaw/errors";
import { IPackingAlgorithm } from "./Packing/common";
import { sortBy } from "client_legacy/ndaw/arrayHelpers";


interface GridItem<TItem> extends GridArea {
	item: TItem;
}

class GridState<TItem> {
	public get dimension(): GridDimension {

		let columnSpan: number = 0;
		let rowSpan: number = 0;

		for (const gridItem of this._gridItems) {
			columnSpan = Math.max(columnSpan, gridItem.position.column + gridItem.dimension.columnSpan);
			rowSpan = Math.max(rowSpan, gridItem.position.row + gridItem.dimension.rowSpan);
		}
		return {
			columnSpan,
			rowSpan
		};
	}

	private readonly _gridItems: GridItem<TItem>[];
	public get gridItems(): ReadonlyArray<GridItem<TItem>> {
		return this._gridItems;
	}

	constructor(gridItems: GridItem<TItem>[]) {
		if (!gridItems) {
			throw new ArgumentNotSuppliedError("items");
		}

		this._gridItems = gridItems;
	}

	public apply(packingAlgorithm: IPackingAlgorithm): GridState<TItem> {
		packingAlgorithm.applyTo(this);
		return this;
	}

	public freeUpArea(areaToFreeUp: GridArea): GridState<TItem> {
		if (!areaToFreeUp) {
			throw new ArgumentNotSuppliedError("spaceToFreeUp");
		}

		this.freeUpAreaInternal(areaToFreeUp, [], this.dimension.columnSpan);
		return this;
	}

	private freeUpAreaInternal(areaToFreeUp: GridArea, gridItemStackInvolvedInThisAction: GridItem<TItem>[], numberOfColumns: number): void {
		// Sort they items based on column as if they were all on the same row
		const orderedGridItems: GridItem<TItem>[] = sortBy(
			this._gridItems
				.filter((gridItem) => gridItemStackInvolvedInThisAction.indexOf(gridItem) === -1),
			(gridItem) => (gridItem.position.row * this.dimension.columnSpan) + gridItem.position.column
		);

		while (true) {
			let collidedGridItem: GridItem<TItem> | null = null;
			for (const gridItem of orderedGridItems) {
				if (GridState.areasCollide(gridItem, areaToFreeUp)) {
					collidedGridItem = gridItem;
					break;
				}
			}

			if (!collidedGridItem) {
				break;
			}

			// We translate the collided space by 1 row at a time (the smallest unit)
			// to preserve the order of the areas
			collidedGridItem.position.row++;
			this.freeUpAreaInternal(collidedGridItem, [...gridItemStackInvolvedInThisAction, collidedGridItem], numberOfColumns);
		}
	}

	public add(gridItem: GridItem<TItem>): GridState<TItem> {
		if (!gridItem) {
			throw new ArgumentNotSuppliedError("item");
		}

		this._gridItems.push(gridItem);
		return this;
	}

	public removeItem(item: TItem): GridState<TItem> {
		if (!item) {
			throw new ArgumentNotSuppliedError("item");
		}
		const indexOfItem: number = this.getIndexForItem(item);
		this._gridItems.splice(indexOfItem, 1);
		return this;
	}

	public getAreaForItem(item: TItem): GridArea {
		if (!item) {
			throw new ArgumentNotSuppliedError("item");
		}
		const indexOfItem: number = this.getIndexForItem(item);
		const gridItem: GridItem<TItem> = this._gridItems[indexOfItem];
		return {
			position: { ...gridItem.position },
			dimension: { ...gridItem.dimension }
		};
	}

	public listCollisions(area: GridArea): ReadonlyArray<GridItem<TItem>> {
		if (!area) {
			throw new ArgumentNotSuppliedError("area");
		}

		return this._gridItems.filter((gridItem) => GridState.areasCollide(gridItem, area));
	}

	private getIndexForItem(item: TItem): number {
		if (!item) {
			throw new ArgumentNotSuppliedError("item");
		}

		const indexOfItem = this._gridItems.findIndex((gridItem) => gridItem.item === item);

		if (indexOfItem === -1) {
			throw new Error(`Item \`${item}\` cannot be found`);
		}

		return indexOfItem;
	}

	public static areasCollide(lhs: GridArea, rhs: GridArea) {
		if (!lhs) {
			throw new ArgumentNotSuppliedError("lhs");
		}
		if (!rhs) {
			throw new ArgumentNotSuppliedError("rhs");
		}

		const areAfterEachother = lhs.position.column + lhs.dimension.columnSpan <= rhs.position.column
			|| rhs.position.column + rhs.dimension.columnSpan <= lhs.position.column
			|| lhs.position.row + lhs.dimension.rowSpan <= rhs.position.row
			|| rhs.position.row + rhs.dimension.rowSpan <= lhs.position.row;

		return !areAfterEachother;
	}
}

export {
	type GridItem,
	GridState
};
